import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { RnsEventBus } from 'features/call/call-events';
import type { SyncCallTimerRnsEventData } from 'features/call/call-timer/interfaces';
import { useWhileComponentMounted } from 'features/utils/hook';
import { useAppSelector } from 'features/app/hooks';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { isTimerStartedSelector } from 'features/call/call-timer/store';

export const useCallTimer = () => {
  const callId = useSelector(callIdSelector);
  const isStarted = useAppSelector(isTimerStartedSelector);
  const isFirstCall = useRef(true);
  const [duration, setDuration] = useState(0);

  const whileComponentMounted = useWhileComponentMounted();

  const resetDuration = () => setDuration(0);

  const handleSyncCallTimer = (data: SyncCallTimerRnsEventData) => {
    setDuration(data.duration);
  };

  useEffect(() => {
    return () => {
      resetDuration();
    };
  }, []);

  useEffect(() => {
    if (!isStarted) {
      return;
    }

    const interval = setInterval(
      () => setDuration((prevDuration) => prevDuration + 1),
      1000
    );

    return () => clearInterval(interval);
  }, [isStarted]);

  useEffect(() => {
    if (isFirstCall.current) {
      isFirstCall.current = false;
      return;
    }

    resetDuration();
  }, [callId]);

  useEffect(() => {
    const subscription = RnsEventBus.$syncCallTimer
      .pipe(whileComponentMounted())
      .subscribe(handleSyncCallTimer);

    return () => subscription.unsubscribe();
  }, [whileComponentMounted]);

  return { duration, setDuration };
};
